import './NotificationCenter.less';

import { useState } from 'react';
import { Badge, Button, Empty, Popover } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { BellOutlined, CloseCircleFilled } from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import {
  useMarkNotificationEventsSeen,
  useNotificationEvents,
} from 'hooks/notificationEvents';
import { NotificationEvent } from 'types/notification';

const FAILED: Record<NotificationEvent['actionType'], string> = {
  CLUSTER_CONNECT: 'Connect failed',
  CLUSTER_UPGRADE: 'Upgrade failed',
  CLUSTER_BACKUP: 'Backup failed',
};

function NotificationCenter({
  className,
  placement,
}: Readonly<{ className: string; placement: TooltipPlacement }>) {
  const [open, setOpen] = useState(false);
  const { data } = useNotificationEvents();
  const { mutate: markSeen } = useMarkNotificationEventsSeen();

  const events = data?.data.events ?? [];
  const unread = data?.data.unread ?? 0;

  // Closing is the acknowledgement: what was new stays marked while it is read.
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next && unread > 0 && events.length) markSeen(events[0].completedAt);
  }

  // Newest first, so the unread ones are always the first `unread` rows.
  const content = events.length ? (
    <ul className="NotificationCenter__list">
      {events.map((event, index) => (
        <li
          key={event.id}
          className={`NotificationCenter__item${
            index < unread ? ' NotificationCenter__item--unread' : ''
          }`}
        >
          <CloseCircleFilled className="NotificationCenter__icon" />
          <div className="NotificationCenter__body">
            <div className="NotificationCenter__heading">
              <span className="NotificationCenter__cluster">
                {event.cluster}
              </span>
              <span className="NotificationCenter__time">
                {formatDistanceToNow(new Date(event.completedAt), {
                  addSuffix: true,
                })}
              </span>
            </div>
            <div className="NotificationCenter__action">
              {FAILED[event.actionType]}
            </div>
            <div className="NotificationCenter__title">{event.title}</div>
          </div>
        </li>
      ))}
    </ul>
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No failed cluster actions in the last 30 days"
    />
  );

  return (
    <Popover
      title="Notifications"
      content={content}
      trigger="click"
      placement={placement}
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="NotificationCenter__popover"
    >
      <Button type="text" aria-label="Notifications" className={className}>
        <Badge count={unread} size="small" offset={[4, -2]}>
          <BellOutlined className="NotificationCenter__bell" />
        </Badge>
      </Button>
    </Popover>
  );
}

export default NotificationCenter;
